// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';

// bernie
import { AnalyticsStore, ContextStore } from 'bernie-core';

// shared-ui
import { ILocalization, LocalizedComponentProps, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { Program } from 'models';

// ui
import { UitkPrimaryButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkText } from 'uitk-react-text';
import { UitkIcon } from 'uitk-react-icons';

// util
import { modifyLocaleParam } from '../../../util/LocaleParamTransformer';
import { getProgramAnalyticsMicroMessage } from '../../../util/analytics';

export interface ProgramCardFooterProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  camref?: string;
  context?: ContextStore;
  layout: string;
  isProgramApproved: boolean;
  l10n?: ILocalization;
  partnerId: string;
  partnerSignedUp: boolean;
  program: Program;
  programStatus?: string;
  programStatusVerified?: boolean;
}

export const ProgramCardFooter: React.FunctionComponent<ProgramCardFooterProps> = ({
  analytics,
  camref,
  context,
  layout,
  isProgramApproved,
  l10n,
  partnerId,
  partnerSignedUp,
  program,
  programStatus,
  programStatusVerified,
}) => {
  const isCard = layout === 'card';

  const renderCardUser = () => {
    if (!partnerSignedUp && !programStatusVerified) {
      return <></>;
    }

    // The following condition is true when user has just added the Company ID and the company ID added already has VERIFIED status in the DB.
    if (programStatusVerified) {
      return (
        <UitkText size={200}>
          <LocalizedText message="programs.cards.content.cj.status_success.verification_successful" />{' '}
        </UitkText>
      );
    }

    let programStatusMessage = (
      <>
        <UitkLayoutFlex direction="column" alignItems={isCard ? 'start' : 'end'}>
          <UitkLayoutFlexItem>
            <UitkText size={200}>{l10n.formatText('programs.cards.content.partnerize_tracking_code')}</UitkText>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkText>{camref}</UitkText>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </>
    );

    if (program.network === 'CJ') {
      programStatusMessage = (
        <>
          <UitkLayoutFlex direction="column" alignItems={isCard ? 'start' : 'end'}>
            <UitkLayoutFlexItem>
              <UitkMoreInfoTrigger>
                <a href="#">
                  <UitkText size={200}>
                    {l10n.formatText(`programs.cards.content.cj.${programStatus.toLowerCase()}_for_cid`)}
                    <UitkIcon name="info_outline" />
                  </UitkText>
                </a>
              </UitkMoreInfoTrigger>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkText>{partnerId}</UitkText>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </>
      );
    }

    return (
      <>
        {programStatusMessage}
        {program.network === 'CJ' && (
          <UitkSpacing padding={{ blockstart: 'three' }}>
            <UitkText size={200}>
              {programStatus === 'EXPIRED' && (
                <>
                  {' '}
                  <UitkSpacing padding={{ blockstart: 'three' }}>
                    <UitkLink inline={true}>
                      <a href={program.signInLink}>
                        <LocalizedText message="programs.cards.content.cj.signin_link" />
                      </a>
                    </UitkLink>
                  </UitkSpacing>
                </>
              )}
            </UitkText>
          </UitkSpacing>
        )}
      </>
    );
  };

  if (program.live) {
    const handleClick = () => {
      if (typeof analytics?.trackEvent === 'function') {
        analytics.trackEvent('affhub_program_products.selected', 'AffHub Program Products Selected', null, null, true, [
          getProgramAnalyticsMicroMessage(program, programStatus),
        ]);
      }
    };

    let viewOrJoinButton;

    if (isProgramApproved) {
      viewOrJoinButton = (
        <Link
          to={context?.locale ? `/${modifyLocaleParam(context.locale)}/products?programName=${program.name}` : null}
          className="a--no-hover"
        >
          <UitkPrimaryButton onClick={handleClick} inverse={isProgramApproved ? false : true}>
            {l10n.formatText('programs.cards.button.go_to_products')}
          </UitkPrimaryButton>
        </Link>
      );
    } else if (programStatus === null || (programStatus && programStatus === 'UNKNOWN')) {
      viewOrJoinButton =
        program.network === 'PARTNERIZE' ? (
          <Link
            to={context?.locale ? program.signUpLink?.replace(':localeName', modifyLocaleParam(context.locale)) : null}
            className="a--no-hover"
          >
            <UitkPrimaryButton>{l10n.formatText('common.join_program')} </UitkPrimaryButton>
          </Link>
        ) : (
          <Link to={{ search: `pwaDialog=joinAffiliateNetwork-${program.name}` }} className="a--no-hover">
            <UitkPrimaryButton>{l10n.formatText('common.join_program')}</UitkPrimaryButton>
          </Link>
        );
    }

    return (
      <>
        <UitkLayoutFlexItem alignSelf={isCard ? 'start' : 'end'}>{renderCardUser()}</UitkLayoutFlexItem>
        {viewOrJoinButton && <UitkLayoutFlexItem>{viewOrJoinButton}</UitkLayoutFlexItem>}
      </>
    );
  }

  // program not live
  return (
    <UitkPrimaryButton disabled={true} inverse={true}>
      {l10n.formatText('common.coming_soon')}
    </UitkPrimaryButton>
  );
};
